<template>
	<div class="c-image-gallery-container" :class="{ 'c-image-gallery-container--single' : !multipleImages }">
		<div class="c-image-gallery" ref="scrollContainer">
			<template v-if="galleryItems.length">
				<slot v-for="(mediaItem, index) in mediaItemsToShow">
					<gallery-image
						v-if="mediaItem.MediaType === 'Image'"
						:image="mediaItem"
						:index="index"
						:product-name="productName"
						:full-width="index === 0 && renderFirstImageFullWidth" />
					<gallery-video
						v-else
						:video="mediaItem"
						:index="index"
						:full-width="index === 0 && renderFirstImageFullWidth" />
				</slot>
			</template>
			<gallery-manufacturer-image
				v-if="!mediaItemsToShow.length && manufactureInfo?.BaseViewModel.DisplayManufactureIcon"
				:manufacturer-data="manufactureInfo?.BaseViewModel"
			 />
			<div v-if="brandTileUrl" class="c-image-gallery__item" :class="{ '-full-width' : renderBrandTileFullWidth }">
				<a class="c-image-gallery__brand-tile" :href="brandTileUrl" :title="brandTileBrandName" data-gtm-event="brand-tile-click">
					<div class="c-image-gallery__brand-tile-pre">{{ brandTileTextPre }}</div>
					<div class="c-image-gallery__brand-tile-name">{{ brandTileBrandName }}</div>
					<div class="c-image-gallery__brand-tile-post">{{ brandTileTextPost }}</div>
				</a>
			</div>
		</div>
		<transition name="fadein" :duration="200">
			<gallery-images-modal v-if="this.galleryImageModalVisible" />
		</transition>
	</div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import GalleryImagesModal from "shared/components/product/gallery/galleryImagesModal.vue";
import GalleryImage from 'shared/components/product/gallery/galleryImage.vue';
import GalleryVideo from 'shared/components/product/gallery/galleryVideo.vue';
import GalleryManufacturerImage from "shared/components/product/gallery/galleryManufacturerImage.vue";

export default {
	components: {
		GalleryImagesModal,
		GalleryImage,
		GalleryVideo,
		GalleryManufacturerImage,
	},
	props: [
		'mediaSources',
		'freeSampleLabel',
		'canSample',
		'labels',
		'brandTileUrl',
		'brandTileBrandName',
		'brandTileTextPre',
		'brandTileTextPost',
		'productName',
		'manufactureInfo'
	],
	data() {
		return {
			index: 0,
		};
	},
	methods: {
		...mapMutations([
			'setGalleryItems',
			'setDisplayManufacturerLogo'
		]),
		selectImage(i) {
			this.index = i;
		},
		...mapMutations([
			'setGalleryImagesModalContent'
		]),
		updateGalleryItems() {
			this.setGalleryImagesModalContent(this.mediaItemsToShow);
			this.setGalleryItems(this.mediaItemsToShow);
		},
	},
	computed: {
		...mapGetters({
			galleryImageModalVisible: 'getGalleryImagesModalVisible',
			galleryItems: 'getGalleryItems',
			selectedVariant: 'getSelectedVariant'
		}),
		multipleImages() {
			return this.mediaItemsToShow && this.mediaItemsToShow.length > 1;
		},
		mediaItems() {
			if (Array.isArray(this.mediaSources?.BaseViewModel)) {
				return this.mediaSources.BaseViewModel || [];
			}
			return [];
		},
		selectedImage() {
			if (this.mediaItemsToShow && this.mediaItemsToShow.length > this.index) {
				return this.mediaItemsToShow[this.index];
			}
			return null;
		},
		renderFirstImageFullWidth() {
			return this.mediaItemsToShow && this.mediaItemsToShow.length === 1;
		},
		renderBrandTileFullWidth() {
			if (this.mediaItemsToShow) {
				return this.mediaItemsToShow.length <= 2 || this.mediaItemsToShow.length % 2 === 0;
			}
			return true;
		},
		mediaItemsToShow() {
			const productItems = this.mediaItems?.filter(source => source.EntryContentType === 1);
			const coloredItems = this.mediaItems?.filter(source => source.EntryContentCode === this.selectedVariant?.Item.Code) || [];

			return coloredItems.length ? coloredItems : productItems;
		},
	},
	watch: {
		mediaItemsToShow() {
			this.updateGalleryItems();
		},
	},
	mounted() {
		this.updateGalleryItems();
		this.setDisplayManufacturerLogo(!this.mediaItemsToShow.length && this.manufactureInfo?.BaseViewModel.DisplayManufactureIcon);
	}
}
</script>

<style lang="scss">
@use 'sass:math';
@import "multisite/styles/settings/settings";
@import "multisite/styles/tools/tools";

.c-image-gallery-container {
	&--single {
		.c-image-gallery {
			grid-template-columns: 1fr;
			margin: 0;
		}
	}
}

.c-image-gallery {
	$section-gutter: 24px;
	$s-play: 60px;
	$s-close: 36px;
	$s-iframe-width: 960px;
	display: flex;
	flex-wrap: nowrap;
	gap: 12px;
	margin-inline-start: -24px;
	margin-inline-end: -24px;
	max-width: 100vw;
	-webkit-overflow-scrolling: touch;
	overflow-x: auto;
	padding-inline: 24px;
	scroll-snap-type: x mandatory;
	scroll-padding-inline: 24px;

	@media (min-width: ds-breakpoint("md")) {
		display: grid;
		gap: 24px;
		grid-template-columns: 1fr 1fr;
		margin: 0;
		padding-inline: 0;
		overflow: hidden;
	}

	&__item {
		flex: 1 0 100%;
		flex-basis: 80%;
		max-width: 216px;
		scroll-snap-align: start;
		width: 100%;

		@media (min-width: ds-breakpoint("md")) {
			max-width: 100%;
		}

		&-full-width {
			grid-column: span 2;
		}
	}

	&__image-figure {
		display: flex;
		justify-content: center;
		align-items: center;
		margin: 0;
		max-width: 100%;
		flex-direction: column;
		position: relative;
		border-radius: 8px;
		overflow: hidden;
		border: solid 1px ds-color("border", "muted");

		&:hover {
			border-color: ds-color("border", "default");

			img {
				transform: scale(1.05);
			}
		}
	}

	&__can-sample {
		position: absolute;
		top: 0;
		right: 0;
		display: block;
		background: ds-color("bg", "brand");
		color: ds-color("fg", "on-color");
		font-size: t-rem(14px);
		font-family: ds-font-family("heading");
		padding: ds-space(3) t-rem(7px);
	}

	&__image {
		height: 100%;
		width: 100%;
		object-fit: fill;
		z-index: -1;
		background: ds-color("bg", "neutral-subtle");
		transition: 0.2s;
	}

	&__thumbnails {
		display: flex;
		flex-wrap: wrap;
		flex: 1 0 auto;
		justify-content: center;

		&.-grid {
			display: grid;
			grid-template-columns: 1fr 1fr 1fr 1fr 1fr;

			@include targetIE {
				display: flex;
				max-width: t-rem(400px);
				justify-content: flex-start;
			}
		}
	}

	&__thumbnail {
		max-height: 100%;
		max-width: 100%;
	}

	&__thumbnail-figure {
		width: t-rem(65px);
		height: t-rem(65px);
		display: flex;
		flex: 0 0 auto;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		padding: t-rem(5px);
		margin: t-rem(5px);
		overflow: hidden;
		border: solid 1px ds-color("border", "muted");
		border-radius: t-rem(4px);

		&.-selected {
			border-color: ds-color("border", "interactive");
		}

		@include hover {
			border-color: ds-color("border", "interactive");
		}
	}

	&__fig-container {
		position: relative;
		cursor: pointer;
	}

	&__play-btn {
		background: ds-color("bg", "neutral-subtle");
		background-clip: padding-box;
		border: solid 1px rgba(ds-color-raw("fg", "default"), .04);
		border-radius: math.div($s-play, 2);
		box-shadow: $shadow-button;
		cursor: pointer;
		display: block;
		height: $s-play;
		left: 50%;
		position: absolute;
		top: 50%;
		transform: translate(-50%, -50%);
		width: $s-play;
	}

	&__play-btn-label {
		background: transparent t-icon("play-btn-product-page", ds-color-action-raw("primary", "default", "text-color")) 50% 50% no-repeat;
		bottom: 0;
		left: 0;
		overflow: hidden;
		position: absolute;
		right: 0;
		text-indent: -500px;
		top: 0;
	}

	&__close-btn-label {
		background: transparent t-icon("close", ds-color-action-raw("primary", "default", "text-color")) 50% 50% no-repeat;
		background-size: 40%;
		bottom: 0;
		left: 0;
		overflow: hidden;
		position: absolute;
		right: 0;
		text-indent: -500px;
		top: 0;
	}

	&__video-overlay {
		display: none;

		&.-show {
			display: block;
			z-index: 10;
		}

		&.-iphone {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;

			iframe {
				width: 100%;
				height: 100%;
			}
		}

		&.-desktop {
			align-items: center;
			background: rgba(0, 0, 0, 0.85);
			flex: 1 0 auto;
			justify-content: center;
			position: fixed;
			top: 0;
			right: 0;
			bottom: 0;
			left: 0;
			z-index: 100;

			&.-show {
				display: flex;
			}

			iframe {
				width: $s-iframe-width;
				height: $s-iframe-width * math.div(9, 16);
			}
		}
	}

	&__close-btn {
		display: block;
		width: $s-close;
		height: $s-close;
		border-radius: math.div($s-close, 2);
		background-color: ds-color-action("primary", "default", "bg-color");
		border: solid 1px ds-color("border", "interactive");
		position: absolute;
		top: calc(50vh - (960px/2/16*9) - 18px);
		right: calc(50vw - (960px/2) - 25px);
	}

	&__brand-tile {
		font-family: ds-font-family("heading");
		align-items: center;
		background-color: ds-color("bg", "interactive-muted");
		border: solid 1px ds-color("border", "muted");
		color: ds-color("fg", "default");
		display: flex;
		flex: 1 0 auto;
		flex-direction: column;
		height: 100%;
		justify-content: center;
		padding: 1.5*$section-gutter $section-gutter;
		text-decoration: none;

		@include hover {
			color: ds-color("fg", "default");
		}
	}

	&__brand-tile-pre,
	&__brand-tile-post {
		font-size: t-rem(20px);
		font-weight: ds-font-weight("medium");
		line-height: 1.2;
	}

	&__brand-tile-name {
		font-size: t-rem(32px);
		font-weight: ds-font-weight("bold");
		line-height: 1.2;
		text-align: center;
	}
}
</style>
